import { Phone, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { breadcrumbSchema, seoKeywords } from '../utils/seoData';

const Areas = () => {
  const mainAreas = [
    {
      name: 'Hebbal',
      localities: ['Nagavara', 'Hebbal Kempapura', 'Ganganagar'],
    },
    {
      name: 'Banaswadi',
      localities: ['Kammanahalli', 'Kalyan Nagar', 'HRBR Layout'],
    },
    {
      name: 'Marathahalli',
      localities: ['Kadubeesanahalli', 'Bellandur', 'Devarabisanahalli'],
    },
    {
      name: 'Whitefield',
      localities: ['Varthur', 'Hoodi', 'Graphite India'],
    },
    {
      name: 'KR Puram',
      localities: ['Mahadevapura', 'Seegehalli', 'Ramamurthy Nagar'],
    },
    {
      name: 'Indiranagar',
      localities: ['HAL Airport', 'Domlur', 'CV Raman Nagar'],
    },
  ];

  const allLocalities = [
    'Hebbal', 'Nagavara', 'Ganganagar', 'Yelahanka',
    'Banaswadi', 'Kammanahalli', 'Kalyan Nagar', 'HBR Layout',
    'Babusapalya', 'Lingarajapuram', 'Kacharkanahalli',
    'Marathahalli', 'Bellandur', 'Kadubeesanahalli',
    'Whitefield', 'Varthur', 'Hoodi', 'Graphite India',
    'KR Puram', 'Mahadevapura', 'Ramamurthy Nagar',
    'Indiranagar', 'Domlur', 'HAL Airport', 'CV Raman Nagar',
    'Thippasandra', 'New Thippasandra', 'C V Raman Nagar',
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Snake Catcher Near Me | Areas in Bangalore We Serve 24/7"
        description="Snake catcher near you across Bangalore - Horamavu, Babusapalya, Kalyan Nagar, Hennur, Ramamurthy Nagar, Banaswadi, HRBR Layout & Kammanahalli. Call now."
        keywords={seoKeywords.areas}
        canonical="/areas"
        schemaData={[breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Areas We Serve', path: '/areas' }
        ])]}
      />
      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <MapPin className="inline h-4 w-4 mr-1" />
              Service Coverage
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-emerald-600">Snake Catcher Near Me</span> — Areas We Serve in Bangalore
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Fast snake rescue near you across Bangalore. Expert rescuers Alhan Nawaz and Mohan K cover Horamavu, Babusapalya, Kalyan Nagar, Hennur, Ramamurthy Nagar, Banaswadi, HRBR Layout, Kammanahalli and all of East & Central Bangalore.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="tel:+918310755854">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Phone className="mr-2 h-5 w-5" />
                  Alhan: +91 8310755854
                </Button>
              </a>
              <a href="tel:+919845080903">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Phone className="mr-2 h-5 w-5" />
                  Mohan: +91 9845080903
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Primary Service Areas</h2>
              <p className="text-gray-600">We provide immediate response in these major localities</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mainAreas.map((area, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow border-emerald-100">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <MapPin className="h-6 w-6 text-emerald-600" />
                      <h3 className="text-xl font-bold text-gray-900">{area.name}</h3>
                    </div>
                    <ul className="space-y-2">
                      {area.localities.map((locality, idx) => (
                        <li key={idx} className="text-gray-700 text-sm flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                          {locality}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All Localities */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">All Covered Localities</h2>
              <p className="text-gray-600">Complete list of areas in our service coverage</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allLocalities.map((locality, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border border-emerald-100 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{locality}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Map Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-emerald-200">
              <CardContent className="p-8">
                <div className="text-center">
                  <MapPin className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Service Coverage Details</h3>
                  <p className="text-gray-700 mb-4">
                    We provide emergency snake rescue services within a <span className="font-semibold text-emerald-600">10km radius from Babusapalya (560043)</span>, covering most of Bangalore East.
                  </p>
                  <p className="text-gray-700 mb-6">
                    This includes areas from <span className="font-semibold">Hebbal</span> in the north to <span className="font-semibold">KR Puram</span> in the east, and from <span className="font-semibold">Central Bangalore</span> all the way to <span className="font-semibold">Kempegowda International Airport</span> region.
                  </p>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-emerald-800 font-semibold">
                      Don't see your area listed? Call us anyway! We may still be able to help or recommend a trusted professional in your area.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quick Response */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Fast Response Matters
            </h2>
            <p className="text-gray-600 mb-8">
              When you discover a snake in your home, every minute counts. Our strategic location in Babusapalya allows us to reach most areas in Bangalore East within 20-30 minutes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-emerald-100">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">20-30</div>
                  <p className="text-sm text-gray-600">Minutes Average Response Time</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-100">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">24/7</div>
                  <p className="text-sm text-gray-600">Available Every Day</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-100">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">100%</div>
                  <p className="text-sm text-gray-600">Safe Removal Success Rate</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Snake Rescue in Your Area?
          </h2>
          <p className="text-xl mb-8 text-emerald-50">
            Call us now for immediate assistance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:+918310755854">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Alhan: +91 8310755854
              </Button>
            </a>
            <a href="tel:+919845080903">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Mohan: +91 9845080903
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Areas;